import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCategories() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState('');

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories`, { credentials: 'include' });
      if (res.status === 401) { navigate('/login'); return; }
      setCategories(await res.json());
    } catch {
      setError(t('admin.categoriesLoadError'));
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    loadCategories();
  }, [authLoading, user, navigate, loadCategories]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: newId.trim(), name: newName.trim(), icon: newIcon.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.detail || t('admin.createError')); return; }
      setNewId(''); setNewName(''); setNewIcon('');
      await loadCategories();
    } catch {
      setCreateError(t('admin.connectionErrorShort'));
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (cat) => {
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories/${cat.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !cat.is_active }),
      });
      // Antes: sin chequear res.ok. Un 403/500 pasaba desapercibido y la UI
      // recargaba la lista como si el toggle hubiera funcionado.
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || t('admin.categoryUpdateError', 'No se pudo actualizar la categoría.'));
        return;
      }
      await loadCategories();
    } catch {
      setError(t('admin.connectionErrorShort'));
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(t('admin.categoryDeleteConfirm', '¿Eliminar la categoría "{{name}}" definitivamente?', { name: cat.name }))) return;
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories/${cat.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || t('admin.categoryDeleteError', 'No se pudo eliminar la categoría.'));
        return;
      }
      await loadCategories();
    } catch {
      setError(t('admin.connectionErrorShort'));
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditIcon(cat.icon || '');
  };

  const handleSaveEdit = async (id) => {
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName.trim(), icon: editIcon.trim() || null }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || t('admin.categoryUpdateError', 'No se pudo actualizar la categoría.'));
        return;
      }
      setEditingId(null);
      await loadCategories();
    } catch {
      setError(t('admin.connectionErrorShort'));
    }
  };

  if (authLoading || loading) {
    return <AdminLoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <AdminTabs />
      <div className="container mx-auto px-4 md:px-6 max-w-3xl py-8">
        <button onClick={() => navigate('/admin/dashboard')} className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#134168] font-medium transition-colors">
          {t('admin.backToPanelShort')}
        </button>
        <h1 className="text-3xl font-bold mb-2">{t('admin.categoriesAssets')}</h1>
        <p className="text-gray-500 mb-8 text-sm">{t('admin.categoriesIntro')}</p>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        <form onSubmit={handleCreate} className="bg-white rounded-2xl p-6 shadow-sm mb-8">
          <h2 className="font-semibold text-lg mb-4">{t('admin.newCategory')}</h2>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('admin.idSlug')}</label>
              <input
                required
                placeholder="car, bicycle..."
                value={newId}
                onChange={e => setNewId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#134168]"
              />
              <p className="text-xs text-gray-400 mt-1">{t('admin.idHint')}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('admin.nameVisible')}</label>
              <input
                required
                placeholder="Car, Bicycle..."
                value={newName}
                onChange={e => setNewName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#134168]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('admin.iconEmoji')}</label>
              <input
                placeholder="🚗"
                value={newIcon}
                onChange={e => setNewIcon(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#134168]"
              />
            </div>
          </div>
          {createError && <p className="text-red-500 text-sm mb-2">{createError}</p>}
          <button
            type="submit"
            disabled={creating}
            className="bg-[#134168] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#39b4aa] transition-all disabled:opacity-50"
          >
            {creating ? t('admin.creating') : t('admin.createCategory')}
          </button>
        </form>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 font-medium text-gray-600">{t('admin.categoryCol')}</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">{t('admin.slugCol')}</th>
                <th className="text-center px-5 py-3 font-medium text-gray-600">{t('admin.assetsCol')}</th>
                <th className="text-center px-5 py-3 font-medium text-gray-600">{t('admin.stateCol')}</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => (
                <tr key={cat.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-5 py-3">
                    {editingId === cat.id ? (
                      <div className="flex gap-2">
                        <input value={editIcon} onChange={e => setEditIcon(e.target.value)} placeholder="🚗" className="w-12 px-2 py-1 border border-gray-200 rounded-lg text-sm" />
                        <input value={editName} onChange={e => setEditName(e.target.value)} className="flex-1 px-2 py-1 border border-gray-200 rounded-lg text-sm" />
                      </div>
                    ) : (
                      <span>{cat.icon && <span className="mr-2">{cat.icon}</span>}{cat.name}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-400 font-mono text-xs">{cat.id}</td>
                  <td className="px-5 py-3 text-center text-gray-600">{cat.asset_count}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {cat.is_active ? t('admin.stateActiveF') : t('admin.stateInactiveF')}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {editingId === cat.id ? (
                        <>
                          <button onClick={() => handleSaveEdit(cat.id)} className="text-xs bg-[#134168] text-white px-3 py-1 rounded-full hover:bg-[#39b4aa]">{t('admin.saveBtn')}</button>
                          <button onClick={() => setEditingId(null)} className="text-xs text-gray-500 hover:text-gray-700">{t('admin.cancelBtn')}</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(cat)} className="text-xs text-[#134168] hover:underline">{t('admin.editBtn')}</button>
                          <button
                            onClick={() => handleToggle(cat)}
                            className={`text-xs px-3 py-1 rounded-full border transition-all ${cat.is_active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}
                          >
                            {cat.is_active ? t('admin.deactivate') : t('admin.activate')}
                          </button>
                          {/* Borrado real solo si no hay vehículos usando la categoría —
                              el backend igual lo valida, pero ocultar el botón evita el
                              error 409 en el caso común (categoría en uso). */}
                          {cat.asset_count === 0 && (
                            <button
                              onClick={() => handleDelete(cat)}
                              className="text-xs px-3 py-1 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-red-600 transition-all"
                            >
                              {t('admin.deleteBtn', 'Eliminar')}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
